<template>
  <v-card class="messages-container fill-height">
    <v-card-text class="messages pa-0" id="messages" @scroll="onScroll" ref="messages">
      <v-list ref="messagesList" class="pb-0">
        <div v-for="(msg, idx) in groupedMessages" :key="idx">
          <!-- the red middle marker -->
          <div v-if="msg.marker || msg.type==='marker'" id="marker" ref="marker" class="mx-3">
            <span>New Messages <v-icon>mdi-chevron-down</v-icon></span>
          </div>

          <v-list-item v-else-if="msg.type === 'pending_chat'" :style="getMessageStyle(msg)">
            <v-list-item-content>
              <v-list-item-title style="text-align: right">
                <span style="cursor: pointer;">
                  {{ msg.name }} - {{ getTime(msg.ts) }}
                </span>
              </v-list-item-title>
              <v-list-item-subtitle class="mt-2 messages-list">
                <p v-for="(line, i) of msg.lines" :key="i" style="text-align: right; white-space: break-spaces;">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" class="mr-2" small :color="isFailedMsg(msg) ? 'red' : 'grey'">
                        <template v-if="isFailedMsg(msg)">
                          mdi-close
                        </template>
                        <template v-else>
                          mdi-clock-alert
                        </template>

                      </v-icon>
                      <span :style="isFailedMsg(msg) ? 'color: red' : 'color: grey'">{{ line.text }}</span>
                    </template>
                    <span v-if="isFailedMsg(msg)">Failed</span>
                    <span v-else>Pending</span>
                  </v-tooltip>
                </p>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-else class="px-5" :style="getMessageStyle(msg)">
            <v-list-item-content>
              <template v-if="msg.type==='handend'">
                <v-list-item-title style="color: deepskyblue">
                  {{ getTime(msg.ts) }} -
                  <template v-if="msg.end > msg.hand_num">
                    <a @click="loadPastHand(msg.end, msg.end)">Hand #{{ msg.hand_num }} - {{msg.end}} Finished</a>
                    <div v-if="msg.text" class="handend-div">
                      <p v-for="(txt, idx) of msg.text.split('\n')" :key="idx" :class="[getHandendClass(txt)]">{{ getParsedHandendText(txt) }}</p>
                    </div>
                  </template>
                  <template v-else>
                    <a @click="loadPastHand(msg.hand_num, msg.end)">Hand #{{ msg.hand_num }} Finished</a>
                    <div v-if="msg.text" class="handend-div">
                      <p v-for="(txt, idx) of msg.text.split('\n')" :key="idx" :class="[getHandendClass(txt)]">{{ getParsedHandendText(txt) }}</p>
                    </div>
                  </template>
                </v-list-item-title>
              </template>
              <template v-else-if="msg.type==='suggestion'">
                <div class="chat-suggestion">
                  <span class="mr-2">{{ getTime(msg.ts) }}</span>
                  <span>Suggested: {{ msg.text }}</span>
                </div>
              </template>
              <template v-else-if="msg.type==='warning'">
                <v-list-item-title :style="msg.isDebug ? 'color: pink' : 'color: orange'">
                  <span>{{ getTime(msg.ts) }} - <v-icon small :color="msg.isDebug ? 'pink' : 'orange'">mdi-alert</v-icon> {{ msg.text }}</span>
                </v-list-item-title>
              </template>
              <template v-else-if="msg.type==='game_event'">
                <v-list-item-title style="color: springgreen">
                  <span>{{ getTime(msg.ts) }} - <v-icon small color="springgreen">mdi-alert-circle</v-icon> {{ msg.text }}</span>
                </v-list-item-title>
              </template>

              <template v-else-if="msg.type==='gift_chat'">
                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="primary">mdi-gift</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent a </span>
                  <img style="width: 5em" :src="'/static/imgs/gifts/' + msg.giftId + '.png'"/>
                  <span>({{ msg.giftId }}) to <strong style="font-size: 1.1em"><span :style="msg.targetIsBot ? 'color: limegreen' : ''">{{ msg.targetName }}</span></strong></span>
                </v-list-item-title>
              </template>

              <template v-else-if="msg.type==='emoji_chat'">
                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="primary">mdi-emoticon</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent </span>
                  <img style="width: 5em; margin: 0 10px;" :src="'/static/imgs/emojis/' + getEmojiImage(msg.emojiId) + '.png'"/>
                  <span>({{ msg.text }})</span>
                </v-list-item-title>
              </template>

              <template v-else-if="msg.type==='voice_chat'">
                <v-list-item-title>{{ getTime(msg.ts) }} - <span :style="getSenderTitleStyle(msg)">{{ msg.name }}</span> ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) <v-icon small v-if="getSenderType(msg) !== 'player'" :style="getSenderTitleStyle(msg)">mdi-account-details</v-icon></v-list-item-title>
                <v-list-item-subtitle class="mt-2 messages-list" :style="getSenderTitleStyle(msg)">
                  <template v-if="!msg.filename">Audio not found</template>
                  <template v-else>
                    <audio-player style="max-width: 350px" :file="'/voice/' + msg.filename"></audio-player>
                  </template>
                </v-list-item-subtitle>
              </template>

              <template v-else-if="msg.type==='announcement_chat'">

                <v-list-item-title class="d-flex align-center justify-center">
                  <span>{{ getTime(msg.ts) }} - <v-icon color="yellow">mdi-bullhorn</v-icon> <strong style="font-size: 1.1em">{{ msg.name }}</strong> sent an annoucement</span>
                </v-list-item-title>

                <v-list-item-subtitle class="mt-2 messages-list d-flex flex-column align-center justify-center" :style="getSenderTitleStyle(msg)">
                  <p style="white-space: break-spaces;" class="yellow--text text-body-1 font-weight-bold">
                    <template v-if="msg.name_mentions && msg.name_mentions.length">
                      <span>{{ msg.text.substr(0, msg.text.indexOf(msg.name_mentions[0].match_str))
                      }}</span><span class="mention-highlight">{{
                      msg.text.substring(msg.text.indexOf(msg.name_mentions[0].match_str), msg.text.indexOf(msg.name_mentions[0].match_str) + msg.name_mentions[0].match_str.length)
                      }}</span><span>{{ msg.text.substring(msg.text.indexOf(msg.name_mentions[0].match_str) + msg.name_mentions[0].match_str.length, msg.text.length) }}</span>
                    </template>
                    <span v-else>{{ msg.text }}</span>
                  </p>
                </v-list-item-subtitle>

              </template>

              <template v-else>
                <template v-if="isSenderActiveAccount(msg)">
                  <v-list-item-title style="text-align: right">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on" style="cursor: pointer;">
                          {{ msg.name }} ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) - {{ getTime(msg.ts) }}
                        </span>
                      </template>
                      <span>Sent by <strong class="text-h6 font-weight-bold">{{ msg.senderName }}</strong></span>
                    </v-tooltip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list">
                    <p v-for="(line, i) of msg.lines" :key="i" style="text-align: right; white-space: break-spaces;">
                      {{ line.text }}
                    </p>
                  </v-list-item-subtitle>
                </template>
                <template v-else-if="isSenderOtherBot(msg)">
                  <v-list-item-title style="text-align: center">{{ msg.name }} ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) - {{ getTime(msg.ts) }}</v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list">
                    <p v-for="(line, i) of msg.lines" :key="i" style="text-align: center; white-space: break-spaces;">
                      {{ line.text }}
                    </p>
                  </v-list-item-subtitle>
                </template>
                <template v-else>
                  <v-list-item-title>{{ getTime(msg.ts) }} - <span :style="getSenderTitleStyle(msg)">{{ msg.name }}</span> ( <span v-if="club.currency==='USD'">$</span>{{ msg.result }} ) <v-icon small v-if="getSenderType(msg) !== 'player'" :style="getSenderTitleStyle(msg)">mdi-account-details</v-icon></v-list-item-title>
                  <v-list-item-subtitle class="mt-2 messages-list" :style="getSenderTitleStyle(msg)">
                    <p v-for="(line, i) of msg.lines" :key="i" style="white-space: break-spaces;">
                      <template v-if="line.name_mentions && line.name_mentions.length">
<span>{{ line.text.substr(0, line.text.indexOf(line.name_mentions[0].match_str))
  }}</span><span class="mention-highlight">{{
  line.text.substring(line.text.indexOf(line.name_mentions[0].match_str), line.text.indexOf(line.name_mentions[0].match_str) + line.name_mentions[0].match_str.length)
}}</span><span>{{ line.text.substring(line.text.indexOf(line.name_mentions[0].match_str) + line.name_mentions[0].match_str.length, line.text.length) }}</span>
                      </template>

                      <template v-else>
                        {{ line.text }}
                      </template>
                    </p>
                  </v-list-item-subtitle>
                </template>
              </template>
            </v-list-item-content>
          </v-list-item>
        </div>
      </v-list>
    </v-card-text>
    <v-card-actions class="text d-flex align-center">
      <v-text-field
          ref="textfield"
          @keydown.enter="sendText"
          v-model="text"
          @input="parseText"
          :label="textEntryLabel"
          hide-details
          filled
          :single-line="textEntryLabel=='send text'"
          dense
          :disabled="!canSendChat"
          contenteditable="true"
          :color="specialChatWarning.show ? 'orange' : ''"
          :style="getInputStyle()"
          :append-icon="iconChatAction"
          @click:append="handleChatAction"
      >
      </v-text-field>

      <template v-if="state && state.room && state.room.status === 'PAUSE'">
        <v-btn
          @click="askWaitPause=true" color="orange"
          class="ml-4" style="min-height: 100%"
          :disabled="isWaitingOnPause"
        >
          <template v-if="!isWaitingOnPause">
            Wait Pause
          </template>
          <template v-else>
            Waiting
          </template>
        </v-btn>
        <v-btn @click="askGetUpSafely=true" color="red" style="min-height: 100%">
          Get Up!
        </v-btn>
      </template>

      <v-btn @click="markRead" class="ml-4" style="min-height: 100%" :disabled="!canMarkReadComputed">
        <template v-if="isDisconnected">Disconnected</template>
        <template v-else>Mark Read</template>
      </v-btn>

      <v-btn
        v-if="isSecondaryUser" class="ml-2" style="min-height: 100%"
        color="orange" @click="askHelp=true"
        :disabled="Boolean(activeChatAccount.operator_needs_help) || isDisconnected">
        Help
      </v-btn>
    </v-card-actions>

    <v-card-title class="text-subtitle-2 d-flex justify-center align-center">
      <span>
        <p v-if="isTalkMore" style="text-align: center">
          <v-icon small color="orange">mdi-alert</v-icon>
          <strong style="color: orange">This account should talk more</strong>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on" class="ml-2"
                color="primary" style="cursor: pointer;"
              >mdi-information</v-icon>
            </template>
            <span>{{ clubAccount.talk_more_instructions }}</span>
          </v-tooltip>
        </p>

        <p style="text-align: center" class="mb-0">
          <b style="font-size: 1.2em; font-weight: 900">
            <v-icon small v-if="hasPastNames">mdi-star</v-icon>
            {{ activeChatAccountName }}
          </b>
          <v-btn small icon @click="openPopup">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <span>(<strong v-if="state && state.room">{{ state.room.status }}</strong>)</span>
          <span v-if="state && state.room && state.room.settings" class="ml-2">
            <v-icon small>mdi-account</v-icon>
            <!-- PLO/NLH -->
            <strong v-if="!('p1' in state)">{{ Object.keys(state.players || {}).length }}/{{ state.room.settings.maxPlayer }}</strong>
            <!-- OFC -->
            <strong v-else>
              {{ state.room.players.length }}/{{ state.room.settings.maxPlayer }}
              <span v-if="state.room.settings.ofcType === 'joker'">JOKER</span>
            </strong>
          </span>

          <span v-if="club && club.min_hands_rule"> {{ club.min_hands_rule }}/</span>
          <span v-if="club && club.initial_warning_rule">
            <template v-if="club.initial_warning_rule.warning_quantity_type === 'minutes'">
              {{ club.initial_warning_rule.num_minutes }}m/
            </template>
            <template v-else>
              {{ club.initial_warning_rule.num_hands }}/
            </template>
          </span>
          <span v-if="club && club.initial_warning_rule">{{ initialRuleName(club.initial_warning_rule) }}</span>
        </p>
        <p style="text-align: center" class="mb-0">
          <b>{{ $store.getters.gameType(activeChatAccountName).toUpperCase() }} - <span v-if="club.currency==='USD'">$</span>{{ bb() }} <span v-if="club.currency!=='USD'">{{ club.currency }}</span></b>
          ||
          <span>Result
            <b :class="result(activeChatAccountName) < 0 ? 'red--text': ''">
              <span v-if="result(activeChatAccountName) < 0">-</span><span v-if="club.currency==='USD'">$</span>{{ Math.abs(result(activeChatAccountName)) }}</b>
            in {{ numHands(activeChatAccountName) }} hands</span>
          ||
          <span>Stack: <b><span v-if="club.currency==='USD'">$</span>{{ ourStack() }}</b></span>
        </p>
        <p v-if="isDev" style="text-align: center; color: gray" class="mb-0">
          ({{ activeChatAccount.trainer_version || "Unknown trainer version" }})
        </p>
        <p
          v-if="isDev && state && state.room && state.room.gameCode"
          style="text-align: center; color: gray" class="mb-0">
          Room game code: {{ state.room.gameCode }}
        </p>
      </span>
    </v-card-title>

    <v-dialog max-width="600px" v-model="askSendSure">
      <v-card>
        <v-card-title>
          Warning!
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-center align-center">
              <span>Warning! <strong>{{ sentBy }}</strong> just sent a message on this account:
                <strong>{{ sentMessage }}</strong></span>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="d-flex justify-center align-center">
              <span>Do you still want to send your message?</span>
            </v-col>
            <v-col cols="12" class="d-flex justify-center align-center">
              <span><strong class="text-h6">{{ sentOriginalMessage }}</strong></span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>

          <v-btn text @click="askSendSure = false; cancelText()">No</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="orange" @click="text=sentOriginalMessage; clearPending(); sendText(true); askSendSure = false">
            <v-icon class="mr-2" color="red">mdi-alert</v-icon>
            Yes
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>


    <v-dialog max-width="600px" v-model="dialogSendEmoji.active">
      <v-card>
        <v-card-title>
          Send emoji?
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex align-center mb-0">
              <span>Select an emoji:</span>
            </v-col>
            <v-col cols="12" class="d-flex justify-center align-center pt-0">
              <v-item-group v-model="dialogSendEmoji.emojiId" class="d-flex justify-center align-center" style="flex-wrap: wrap; gap: 5px;">

                <v-item v-for="emojiId of 27" :key="emojiId" v-slot="{ active, toggle }">
                  <v-card @click="toggle" :color="active ? '#1eff9050' : ''" outlined class="d-flex flex-column justify-center align-center py-2">
                    <!-- <v-chip small>{{ gift.id }}</v-chip> -->
                    <img width="100" height="100" :src="'/static/imgs/emojis/' + getEmojiImage(emojiId) + '.png'"/>
                  </v-card>
                </v-item>

              </v-item-group>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>

          <v-btn text @click="dialogSendEmoji.active = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text color="green" @click="finishSendEmoji"
            :disabled="[undefined, null].includes(dialogSendEmoji.emojiId) || !isSitting"
          >
            Send
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>



    <v-dialog max-width="600px" v-model="dialogSendGift.active">
      <v-card>
        <v-card-title>
          Send gift to a player?
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex align-center mb-0 pb-0">
              <span>Select a player:</span>
            </v-col>
            <v-col cols="12" class="d-flex justify-center align-center pt-0">
              <v-chip-group active-class="primary--text" column v-model="dialogSendGift.targetPlayerId">
                <v-chip filter v-for="player of dialogSendGift.players" :key="player.id">{{ player.name }}</v-chip>
              </v-chip-group>
            </v-col>

            <v-col cols="12" class="d-flex align-center mb-0">
              <span>Select a gift:</span>
            </v-col>
            <v-col cols="12" class="d-flex align-center mb-0 justify-center">
              <span>Available chips: <v-chip outlined color="green">{{ availableGiftChips }}</v-chip></span>
            </v-col>
            <v-col cols="12" class="d-flex justify-center align-center pt-0">
              <v-item-group v-model="dialogSendGift.giftId" class="d-flex justify-center align-center" style="flex-wrap: wrap; gap: 5px;">

                <v-item v-for="gift of dialogSendGift.gifts" :key="gift.id" v-slot="{ active, toggle }">
                  <v-card @click="toggle" :color="active ? '#1eff9013' : ''" outlined class="d-flex flex-column justify-center align-center py-2" :disabled="gift.pricePrivate > availableGiftChips">
                    <v-chip small>{{ gift.id }}</v-chip>
                    <img :src="'/static/imgs/gifts/' + gift.id + '.png'"/>
                    <v-chip small outlined color="green">
                      <span>Price: <strong style="font-size: 1.2em">{{ gift.pricePrivate ? gift.pricePrivate : 'FREE' }}</strong></span>
                    </v-chip>
                  </v-card>
                </v-item>

              </v-item-group>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>

          <v-btn text @click="dialogSendGift.active = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text color="green" @click="finishSendGift"
            :disabled="[undefined, null].includes(dialogSendGift.targetPlayerId) || [undefined, null].includes(dialogSendGift.giftId) || roomStatus !== 'PLAYING'"
          >
            Send
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="600px" v-model="askHelp">
      <v-card>
        <v-card-title>
          Do you want to ask for help with this account?
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-center align-center">
             Asking for help with {{ activeChatAccountName }}
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>

          <v-btn text @click="askHelp = false">No</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="green" @click="askForHelp(); askHelp = false">
            Yes
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>


    <v-dialog v-model="askGetUpSafely" width="500">
      <v-card>
        <v-card-title>Confirm Get Up Safely with {{ activeChatAccountName }}?</v-card-title>
        <v-card-text>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="orange" text @click="getUpSafely">Get Up Safely</v-btn>
          <v-btn text @click="askGetUpSafely=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="askWaitPause" width="500">
      <v-card>
        <v-card-title>Confirm wait on the paused table with {{ activeChatAccountName }}?</v-card-title>
        <v-card-text>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="orange" text @click="waitPause">Wait</v-btn>
          <v-btn text @click="askWaitPause=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div
      v-if="dialogFailedSend.active"
      style="position: absolute; bottom: 0; right: 0; left: 0; top: 0;"
      class="d-flex justify-center align-center"
    >
      <v-alert
        v-model="dialogFailedSend.active" type="error" title="Failed to send message"
        dismissible style="max-width: 80%"
      >
        {{ dialogFailedSend.message }}
      </v-alert>
    </div>

  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AudioPlayer from './AudioPlayer.vue';

export default {
  components: { AudioPlayer },

  data() {
    return {
      text: '',
      canMarkRead: true,
      scrolledToMarker: false,
      needScroll: false,

      isTyping: false,
      isTypingTimeout: null,

      askSendSure: false,
      sentBy: null,
      sentMessage: null,
      sentOriginalMessage: null,

      askGetUpSafely: false,
      askWaitPause: false,

      askHelp: false,

      dialogSendGift: {
        active: false,
        targetPlayerId: null,
        giftId: null,
      },

      dialogSendEmoji: {
        active: false,
        emojiId: null,
      },

      dialogFailedSend: {
        active: false,
        message: null,
      },

      specialChatWarning: {
        show: false,
        text: '',
      }
    };
  },

  mounted() {
    setTimeout(() => {
      if (this.$refs.marker) {
        if (this.$refs.textfield) {
          this.$refs.textfield.focus();
        }
        this.scrollToMarker();
        // clearInterval(interval);
      }
    }, 250);
  },

  sockets: {
    chat_is_busy(data) {
      this.sentBy = data.sent_by ? data.sent_by.user : null;
      this.sentMessage = data.sent_message;
      this.sentOriginalMessage = data.message;

      this.askSendSure = true;
    },

    chat_failed(data) {
      this.dialogFailedSend.active = true;
      this.dialogFailedSend.message = data.message;

      // fail pendingMessages
      let messages = [...this.$store.state.pendingMessages[data.displayID] || []];
      // set most recent message as failed, sending chat failed
      if (messages.length) {
        messages[messages.length - 1].failed = true;
        // update this acc pending messages
        this.$store.state.pendingMessages[data.displayID] = messages;
        // force update
        this.$store.state.pendingMessages = {...this.$store.state.pendingMessages}
      }
    },
  },

  computed: {
    iconChatAction() {
      if (this.app === 'Pkr2') {
        return this.roomStatus === 'PLAYING' ? 'mdi-gift' : '';
      } else if (this.app === 'ClubGG') {
        return this.isSitting ? 'mdi-emoticon' : '';
      }

      return '';
    },

    textEntryLabel() {
      if (this.specialChatWarning.show) {
        return this.specialChatWarning.text;
      }

      if (this.someoneElseTyping) {
        return `${this.activeChatAccount.is_typing.user} is currently typing...`;
      }

      /* if (this.isMuted) { */
      /*   return 'Can\'t chat, not seated and viewers are muted'; */
      /* } */

      /* if (!this.canSendChat && this.app === 'ClubGG') { */
      /*   return 'Can\'t chat, not seated'; */
      /* } */

      if (!this.canSendChat) {
        return this.canSendChat__reason || 'Can\'t chat';
      }

      let res = '';
      if (this.onlyRequiredChats) {
        res = 'Limited Chat Account! Only chat if name mentioned or giving warning/asking calltime';
      } else {
        res = 'send text';
      }

      if (this.isDisconnected) {
        res = '(disconnected) ' + res;
      }

      return res;
    },

    someoneElseTyping() {
      let aca = this.activeChatAccount;
      if (!aca) return false;
      if (!aca.is_typing) return false;
      if (aca.is_typing._id === this.activeOperatorId) return false;
      let nowTs = new Date().getTime() / 1000;
      if (aca.is_typing.ts < nowTs - 10) return false;
      return true;
    },

    onlyRequiredChats() {
      return !!(((this.clubAccount || {}).funding_agent || {}).bots_only_required_chats);
    },
    canMarkReadComputed() {
      if (this.isDisconnected) {
        return false;
      }

      if (!this.canMarkRead) {
        // can't mark read for other reasons
        return false;
      }

      // can't mark read if have text to send
      if (this.text.length > 0) {
        return false;
      }

      // ok
      return true;
    },
    groupedMessages() {
      let messages = [...this.messagesForUser(this.activeChatAccountName)];
      let pendingMessages = [...this.pendingMessagesForUser(this.activeChatAccountName)]

      let result = [];
      let last = null;
      let hasMarker = false;
      let afterMarker = 0;
      let markerTs = this.markers(this.activeChatAccountName);

      let allMessages = [...messages, ...pendingMessages];
      allMessages.sort((a, b) => a.ts - b.ts);

      for (let message of allMessages) {
        let addedMessage = false;

        if (message.marker) {
          hasMarker = true;
        }

        // * add marker based on db value
        if (!hasMarker && markerTs && message.ts > markerTs) {
          hasMarker = true;
          result.push({
            type: "marker"
          })
        }
        // * handle marker on not read messages
        else if (!hasMarker && message.type === 'group' && !message.read) {
          hasMarker = true;
          result.push({
            type: "marker"
          })
        }

        if (result.length == 0 || message.marker) {
          last = {...message};
          last.lines = [{
            '_id': last._id,
            'text': last.text,
            'name_mentions': last.name_mentions
          }];
          result.push(last);
          addedMessage = true;
        } else if (message.type === "handend") {
          if (!this.shouldGroupHandend(message, last)) {
            last = {...message};
            result.push(last);
            addedMessage = true;
          } else if (last && last.type === 'handend' && message.hand_num > last.hand_num) {
            last.end = message.hand_num;
            last.text = message.text;
          } else if (last && last.type === 'handend' && message.hand_num == last.hand_num) {
            continue;
          } else {
            last = {...message};
            last.end = last.hand_num;
            result.push(last);
            addedMessage = true;
          }
        } else if (message.type === "warning") {
          if (this.shouldShowWarningMsg(message)) {
            last = {...message};
            last.isDebug = this.isDebugWarningMsg(message);
            result.push(last);
            addedMessage = true;
          }
        } else if (message.type === "game_event") {
          last = {...message};
          result.push(last);
          addedMessage = true;
        } else if (message.type === "suggestion") {
          last = {...message};
          result.push(last);
          addedMessage = true;
        } else if (["voice_chat", "emoji_chat", "gift_chat", "announcement_chat"].includes(message.type)) {
          last = {...message};
          result.push(last);
          addedMessage = true;
        } else {
          if (
            last && last.name === message.name
              && last.result === message.result
              && this.getTime(last.ts) === this.getTime(message.ts)
              && !['voice_chat', 'gift_chat', 'emoji_chat', 'announcement_chat'].includes(last.type)
          ) {
            last.lines.push({
              '_id': message._id,
              'text': message.text,
              'name_mentions': message.name_mentions
            });
          } else {
            last = {...message};
            last.lines = [{
              '_id': last._id,
              'text': last.text,
              'name_mentions': last.name_mentions
            }];
            result.push(last);
            addedMessage = true;
          }
        }

        if (addedMessage && hasMarker && !result[result.length - 1].marker) {
          afterMarker += 1;
        }
        result[result.length - 1].after_marker = afterMarker;
      }

      if (!hasMarker) {
        if (!messages) {
          result.unshift({
            type: "marker"
          })
        } else {
          result.push({
            type: "marker"
          })
        }
      }
      return result;
    },

    availableGiftChips() {
      if (!this.state) return [];
      if (!('gifts_info' in this.state)) return [];

      return this.state.gifts_info.availableChips || 0;
    },

    giftOptions() {
      if (!this.state) return [];
      if (!('gifts_info' in this.state)) return [];

      return this.state.gifts_info.gifts;
    },

    otherPlayers() {
      let aca = this.activeChatAccount;
      if (!this.state) return [];

      let players = [];
      let isOfc = this.isOFC(this.state);

      if (!isOfc) {
        for (let p in this.state.players) {
          let player = this.state.players[p];
          if (player.name === aca.displayID) continue;
          players.push(player);
        }
      } else {
        for (let p of ['p1', 'p2', 'p3']) {
          if (!(p in this.state)) continue;
          let player = this.state[p];
          if (player.name === aca.displayID) continue;
          players.push(player);
        }
      }

      return players;
    },

    automationState() {
      let aca = this.activeChatAccount;
      return aca ? aca.automationState : {};
    },

    nameMentionsByChatId() {
      if (!this.automationState) return {};
      if (!("name_mentions" in this.automationState)) return {}

      let mentions = {};

      for (let mention of this.automationState.name_mentions) {
        mentions[mention.chat_id] = mention;
      }

      return mentions;
    },

    state() {
      return this.accountState(this.activeChatAccountName);
    },

    roomStatus() {
      if (!this.state) return null;
      if (!("room" in this.state)) return null;

      return this.state.room.status;
    },

    isTalkMore() {
      if (!this.clubAccount) return false;
      return this.clubAccount.talk_more;
    },

    clubAccount() {
      return this.clubmgmAccountByName(this.activeChatAccountName);
    },
    club() {
      return (this.clubAccount || {}).club || {};
    },
    app() {
      if (!this.club) return 'Pkr2';
      if (!this.club.app) return 'Pkr2';
      return this.club.app;
    },

    hasPastNames() {
      return this.clubAccount && this.clubAccount.nicknames && this.clubAccount.nicknames.length > 0;
    },

    canSendChat__reason() {
      if (this.isViewerMuted) return "Chat restricted, viewers can't chat";
      if (this.isPlayerMuted) return "Chat restricted, players can't chat";

      if (this.someoneElseTyping) return "Can't chat, someone else is typing";

      // old logic, here for bots that didn't update
      if (this.app === 'ClubGG' && !this.isSitting) return "Can't chat, players can't chat";

      return null;
    },

    canSendChat() {
      if (this.isMuted) return false;
      if (this.someoneElseTyping) return false;

      // old logic, here for bots that didn't update
      if (this.app === 'ClubGG' && !this.isSitting) return false;

      return true;
    },

    isViewerMuted() {
      if (!this.state) return false;
      if (!("room" in this.state)) return false;
      if (!("settings" in this.state.room)) return false;

      return !this.isSitting && this.state.room.settings.viewerMute;
    },

    isPlayerMuted() {
      if (!this.state) return false;
      if (!("room" in this.state)) return false;
      if (!("settings" in this.state.room)) return false;

      return this.isSitting && this.state.room.settings.playerMute;
    },

    isMuted() {
      return this.isViewerMuted || this.isPlayerMuted;
    },

    isSitting() {
      let p = this.ourPlayer(this.activeChatAccountName);

      if (!p) return false;

      if (p.seat === 0) return true;

      if (p.seat && p.seat >= 0) return true;

      return false;
    },

    isSecondaryUser() {
      if (!this.usersOnDuty) return false;
      let onDuty = this.usersOnDuty.filter(el => el._id === this.$store.getters.user._id && el.on_duty_type === 'secondary');
      if (!onDuty || !onDuty.length) return false;
      return true;
    },

    isWaitingOnPause() {
      let now = Date.now();

      if (!this.activeChatAccount.asked_to_wait_paused_table_until_ts) return false;
      if (now >= this.activeChatAccount.asked_to_wait_paused_table_until_ts * 1000) return false;

      return true;
    },

    ...mapGetters([
      'messagesForUser',
      'pendingMessagesForUser',
      'unreadMessages',
      'activeChatAccount',
      'activeChatAccountName',
      'result',
      'accountState',
      'clubmgmAccountByName',
      'isOursByPlayerID',
      'ourPlayer',
      'markers',
      'isClaud',
      'isAlex',
      'isPedro',
      'isDev',
      'isAdmin',
      'isManager',
      'activeOperatorId',
      'usersOnDuty',
    ]),
    ...mapState([
      'isDisconnected',
    ])
  },
  watch: {
    '$store.state.activeChatAccountName': function(newVal, oldVal) {
      if (oldVal !== newVal) {
        setTimeout(() => {
          if (this.$refs.marker) {
            if (this.$refs.textfield) {
              this.$refs.textfield.focus();
            }
            this.scrollToMarker();
            // clearInterval(interval);
          }
        }, 250);
      }
      let m = document.getElementById("messages");
      this.setCanMarkRead(m);
    },
    // groupedMessages() {
    //   if (this.$refs.marker && this.$refs.marker[0]) {
    //     let canScrollToEnd = this.$refs.messages.scrollTop >= (this.$refs.messages.scrollHeight - this.$refs.messages.clientHeight) - 50;
    //     let markerVisible = (this.$refs.marker[0].offsetTop - this.$refs.messages.clientHeight / 10) >= this.$refs.messages.scrollTop;

    //     if (this.$refs.messages.scrollTop + 50 > (this.$refs.marker[0].offsetTop - this.$refs.messages.clientHeight / 10) > this.$refs.messages.scrollTop - 200) {
    //       this.scrollToMarker();
    //     } else if (canScrollToEnd && markerVisible) {
    //       this.scrollToEnd();
    //     }
    //   }
    // }
  },
  methods: {
    getMessageStyle(msg) {
      let style = "";

      if (msg.after_marker === 1) {
        style = "background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(23,187,230,.07) 100%);";
      } else if (msg.after_marker > 1) {
        style = "background: rgba(23,187,230,.07)";
      }

      return style;
    },

    isOFC(state) {
      if (!('p1' in state)) return false;
      return true;
    },

    isDebugWarningMsg(message) {
      if (message.is_debug) {
        return true;
      }

      if (message.text.indexOf("Error:") >= 0) {
        // some errors shouldn't be shown
        if (message.text.indexOf("dummyForNotShowMessageOnUI") >= 0) {
          return true;
        }

        if (message.text.indexOf("NOT_IN_ROOM") >= 0) {
          return true;
        }
      }

      if (message.text.indexOf("(debug)") >= 0) {
        return true;
      }

      return false;
    },
    shouldShowWarningMsg(message) {
      if (message.dev_only) {
        return this.isDev;
      }

      if (this.isDebugWarningMsg(message)) {
        // only claud&alex&pedro sees debug messages
        return this.isClaud || this.isAlex || this.isPedro;
      }

      // if not debug, everyone sees all
      return true;
    },

    floatBB() {
      let d = 1;
      let st = this.state;
      if (!st || !st.settings) return 0;
      if (st.settings.withDecimal) {
        d = 100;
      }
      return st.settings.bb / d;
    },

    bb() {
      let bb = this.floatBB();

      let hasDecimal = bb % 1 > 0;
      let greaterThanOne = bb >= 1;

      bb = hasDecimal ? bb.toFixed(2) : parseInt(bb).toString()

      return greaterThanOne ? bb : bb.substring(1, bb.length - 1);
    },

    initialRuleName(rule) {
      let name = rule.type;
      if (name === "win_gte_bb") {
        return "win>=" + rule.gte_bb;
      } else if (name === "never") {
        return "never";
      } else if (name === "win_gte_chm") {
        return "win>=$" + rule.gte_chm;
      } else if (name === "re_double_up") {
        return "re2x";
      } else if (name === "re_triple_up") {
        return "re3x";
      } else if (name === "custom") {
        return rule.custom;
      } else if (name === "when_winning") {
        return "winning";
      } else if (name === "always") {
        return "always";
      } else if (name === "double_up") {
        return "2x";
      } else if (name === "triple_up") {
        return "3x";
      } else if (name === "play_another_35") {
        return "+35"
      }
      return name;
    },

    getParsedHandendText(text) {
      if (this.club.currency === "USD") return text;

      let newText = text.replace("($", "(").replace(")", ` ${this.club.currency ? this.club.currency : ''})`);

      return newText;
    },

    getHandendClass(text) {
      if (text.includes("won")) {
        if (text.includes("huge")) {
          return "huge";
        } else if (text.includes("large")) {
          return "large";
        }
      }
      else if (text.includes("lost")) {
        if (text.includes("with AA")) {
          return "with-aa";
        } else if (text.includes("with QQ")) {
          return "with-qq";
        } else if (text.includes("with KK")) {
          return "with-kk";
        } else if (text.includes("set over set")) {
          return "set-over-set";
        } else if (text.includes("got unlucky")) {
          return "unlucky"
        }
      }

      return "";
    },
    parseText() {
      if (!this.isTyping) {
        this.$socket.emit("start_typing", {'displayID': this.activeChatAccountName});
      }

      if (this.app != 'ClubGG') {
        let text = this.text;

        // capitalize first letter
        text = this.text.charAt(0).toUpperCase() + this.text.substr(1, this.text.length);

        // capitalize i
        text = text.replaceAll(" i ", " I ");
        // capitalize i'm
        text = text.replaceAll(" i'm ", " I'm ");
        // im to I'm
        text = text.replaceAll(" im ", " I'm ");
        if (text.startsWith("Im ")) {
          text = text.replace("Im ", "I'm ");
        }

        // capitalize after punctuation
        let re = /(^|[.!?]\s+)([a-z])/g;
        text = text.replace(re, (m, $1, $2) => $1 + $2.toUpperCase());

        this.text = text;
      }

      this.isTyping = true;

      // reset timeout
      clearTimeout(this.isTypingTimeout);
      this.isTypingTimeout = setTimeout(() => {
        this.isTyping = false;
        this.$socket.emit("stop_typing", {'displayID': this.activeChatAccountName});
      }, 10000)
    },
    getTime(ts) {
      if (!ts) return;
      var time = ts.toLocaleTimeString();
      var parts = time.split(" ")[0].split(':');
      return parts[0] + ":" + parts[1];
    },

    openSendEmoji() {
      this.dialogSendEmoji = {
        active: true,
        emojiId: null,
        app: this.app,
      }
    },
    finishSendEmoji() {
      this.dialogSendEmoji.active = false;
      // index starts with 0
      this.sendEmoji(this.dialogSendEmoji.emojiId + 1);
    },
    sendEmoji(emojiId) {
      if (this.isDisconnected) {
        return;
      }

      let payload = {
        'displayID': this.activeChatAccountName,
        'emojiId': emojiId.toString(),
      }

      this.$socket.emit("send_emoji", payload);

      setTimeout(() => {
        let m = document.getElementById("messages");
        this.setCanMarkRead(m);
      }, 1000);
    },

    openSendGift() {
      this.dialogSendGift = {
        active: true,
        giftId: null,
        targetPlayerId: null,
        players: [...this.otherPlayers], // save this in case it changes during the usage
        gifts: [...this.giftOptions], // same here
      }
    },

    finishSendGift() {
      let playerId = this.dialogSendGift.players[this.dialogSendGift.targetPlayerId].id;
      let giftId = this.dialogSendGift.gifts[this.dialogSendGift.giftId].id;
      this.dialogSendGift.active = false;
      this.sendGift(playerId, giftId);
    },

    sendGift(toPlayerId, giftId) {
      if (this.isDisconnected) {
        return;
      }

      let payload = {
        'displayID': this.activeChatAccountName,
        'targetPlayerId': toPlayerId,
        'giftId': giftId,
      }

      this.$socket.emit("send_gift", payload);

      setTimeout(() => {
        let m = document.getElementById("messages");
        this.setCanMarkRead(m);
      }, 1000);
    },

    cancelText() {
      let displayID = this.activeChatAccountName;

      // fail pendingMessages
      let messages = [...this.$store.state.pendingMessages[displayID] || []];
      // set most recent message as failed, sending chat failed
      if (messages.length) {
        messages[messages.length - 1].failed = true;
        // update this acc pending messages
        this.$store.state.pendingMessages[displayID] = messages;
        // force update
        this.$store.state.pendingMessages = {...this.$store.state.pendingMessages}
      }
    },

    clearPending() {
      let displayID = this.activeChatAccountName;

      // clear pendingMessages
      let messages = [...this.$store.state.pendingMessages[displayID] || []];
      // remove most recent chat
      if (messages.length) {
        messages.pop()
        // update this acc pending messages
        this.$store.state.pendingMessages[displayID] = messages;
        // force update
        this.$store.state.pendingMessages = {...this.$store.state.pendingMessages}
      }
    },

    sendText(forceSend=false) {
      if (this.isDisconnected) {
        return;
      }

      if (!this.text.trim().length) return;

      if (this.app === 'ClubGG') {
        let emojiRegex = /\p{Extended_Pictographic}/u;

        if (emojiRegex.test(this.text)) {
          if (!this.specialChatWarning.show) {
            setTimeout(() => {
              this.specialChatWarning.show = false;
            }, 5000)
          }

          this.specialChatWarning.show = true;
          this.specialChatWarning.text = "Emoji text not supported on this table";

          return;
        }
      }

      let payload = {
        'displayID': this.activeChatAccountName,
        'text': this.text
      }

      if (forceSend === true) {
        payload.send_anyway = true;
      }

      // add pending message
      this.$store.dispatch("addPendingMessage", {
        'displayID': this.activeChatAccountName,
        'type': 'pending_chat',
        'name': this.activeChatAccountName,
        'text': this.text.trim(),
        'ts': new Date(),
      });

      this.$socket.emit("send_chat", payload);
      this.text = '';
      setTimeout(() => {
        let m = document.getElementById("messages");
        this.setCanMarkRead(m);
      }, 1000);

      // update typing
      this.isTyping = false;
      this.$socket.emit("stop_typing", {'displayID': this.activeChatAccountName});
      clearTimeout(this.isTypingTimeout);
    },

    markRead() {
      let name = this.activeChatAccountName;
      this.$store.commit("markChatRead", name);
    },

    askForHelp() {
      let name = this.activeChatAccountName;
      this.$socket.emit("mark_needs_help", {'displayID': name})
      // update acc asked for help
      this.$store.dispatch("operatorNeedsHelp", name);
      this.$store.commit("invalidateAccountCache");
      this.$store.commit("autoPickActiveChatAccount");
    },


    onScroll(evt) {
      let tgt = evt.target;
      this.setCanMarkRead(tgt);
    },
    setCanMarkRead(tgt) {
      if (!tgt) return;
      let h = tgt.clientHeight;
      let sh = tgt.scrollHeight;
      let st = tgt.scrollTop;
      if (sh <= h) {
        this.canMarkRead = true;
        return;
      }
      if (st + h + 5 >= sh) {
        this.canMarkRead = true;
        return;
      }
      this.canMarkRead = false;
    },
    scrollToMarker() {
      if (this.$refs.marker && this.$refs.marker[0]) {
        // scroll to center
        try {
          // scrollIntoViewIfNeeded is not implemented on some browsers, firefox specifically don't implement it
          // it would more or less make the site unsusable on firefox
          this.$refs.marker[0].scrollIntoView({block: 'center'});
        // adjust offset
          this.$refs.messages.scrollTop = this.$refs.marker[0].offsetTop - this.$refs.messages.clientHeight / 10;
        } catch {
          // pass
        }
      }
    },
    scrollToEnd() {
      try {
        setTimeout(() => {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight - this.$refs.messages.clientHeight;
        }, 1);
      } catch {
        // pass
      }
    },
    loadPastHand(hand_num) {
      // not follow most recent
      this.$store.commit("setFollowMostRecent", {displayID: this.activeChatAccountName, followMostRecent: false});

      // load this past hand
      let room_id = this.$store.state.states[this.activeChatAccountName].room_id;
      let gameType = this.$store.getters.gameType(this.activeChatAccountName);
      this.$store.dispatch("loadHand", {displayID: this.activeChatAccountName, roomId: room_id, gameType: gameType, handNum: hand_num});
    },
    numHands(name) {
      let st = this.accountState(name);
      if (st && st.hands_played) {
        return st.hands_played;
      }
      return 0;
    },
    ourStack() {
      let p = this.ourPlayer(this.activeChatAccountName);
      // console.log(p);
      try {
        let s = this.state.settings;
        let d = 1;
        if (s.withDecimal) {
          d = 100;
        }
        return (p.stack / d).toFixed(2);
      } catch {
        // some error getting stack
      }
      return 0;
    },
    openPopup() {
      window.open(
          `/questions?accountId=${this.clubAccount._id}`,
          "",
          "width=400,height=600,left=200,top=200"
      );
    },
    formatNumber(v, _d=2) {
      if (!v) return v;
      return (v).toFixed(_d).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    getInputStyle() {
      if (this.activeChatAccount.contacting_host_not_available) {
        return 'background-color: #f32;';
      }

      if (this.isHighstakes()) {
        return 'background-color: #f32;';
      }

      if (this.$store.getters.gameType(this.activeChatAccountName) === "ofc") {
        return 'background-color: #0FC6FE;';
      }

      return '';
    },

    currency() {
      return (this.club || {}).currency || 'USD?';
    },

    isHighstakes() {
      let currency = this.currency();

      if (currency === "USD") {
        return this.floatBB() >= 10;
      } else if (currency === "NOK") {
        return this.floatBB() >= 75;
      } else {
        return false;
      }
    },

    shouldGroupHandend(message, last) {
      if (last && last.type === 'handend' && message.group === false) return false;
      if (last && last.group === false) return false;

      return true;
    },


    isSenderActiveAccount(msg) {
      if (msg.senderType === 'bot_current') return true;
      return msg.name === this.activeChatAccountName;
    },
    isSenderOtherBot(msg) {
      if (msg.senderType === 'bot_other') return true;
      return this.isOursByPlayerID(msg.senderId);
    },
    isSenderManager(msg) {
      if (msg.senderType === 'manager') return true;

      if (!this.club) return false;
      if (!this.club.lobby_info) return false;
      if (!this.club.lobby_info.managers) return false;

      return this.club.lobby_info.managers.includes(msg.senderId);
    },
    isSenderOwner(msg) {
      if (msg.senderType === 'owner') return true;

      if (!this.club) return false;
      if (!this.club.lobby_info) return false;
      if (!this.club.lobby_info.managers) return false;

      return this.club.lobby_info.ownerId === msg.senderId;
    },

    getSenderType(msg) {
      if (this.isSenderManager(msg)) return "manager";
      if (this.isSenderOwner(msg)) return "owner";

      return "player";
    },

    getSenderTitleStyle(msg) {
      if (this.isSenderManager(msg)) {
        return "color: #DF80EC";
      }

      if (this.isSenderOwner(msg)) {
        return "color: #EC896A";
      }

      return '';
    },

    getEmojiImage(emojiId) {
      return `emoji_${this.app}_${emojiId}`;
    },

    handleChatAction() {
      if (this.app === 'Pkr2') {
        this.openSendGift();
      } else if (this.app === 'ClubGG') {
        this.openSendEmoji();
      }
    },

    waitPause() {
      this.askWaitPause = false;

      let name = this.activeChatAccountName;
      if (!this.$socket) return;

      let st = this.state;
      if (!st || !st.room_id) {
        return;
      }

      this.$socket.emit('wait_table_pause', {'displayID': name, 'roomId': st.room_id})
      this.$store.commit("invalidateAccountCache");
    },

    getUpSafely() {
      this.askGetUpSafely = false;

      let name = this.activeChatAccountName;
      if (!this.$socket) return;

      let st = this.state;
      if (!st || !st.room_id) {
        return;
      }

      this.$socket.emit('stand_up_safely', {'displayID': name, 'roomId': st.room_id})
      this.$store.commit("invalidateAccountCache");
    },

    secondsBetweenDates(d1, d2) {
      let diff = Math.abs(d1 - d2);
      return Math.floor(diff / 1000);
    },

    isFailedMsg(msg) {
      if (msg.failed) return true;
      return !this.isDisconnected && this.secondsBetweenDates(new Date(), msg.ts) >= 120;
    }

  }
}
</script>

<style scoped>
.messages-container {
  /*height: calc(100vh - 96px) !important;*/
  /*max-height: calc(100vh - 96px) !important;*/
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}

.messages {
  position: relative;
  height: 100% !important;
  /*max-height: 80vh;*/
  overflow-y: auto;
}
.text {
  background-color: #262626;
}

.v-text-field--filled {
  border-radius: 4px !important;
}

.v-text-field--filled >>> .v-input__slot::before {
  border-style: none !important;
}

.v-card {
  /*height: 90vh !important;*/
  /*max-height: 90vh;*/
  overflow-y: auto;
}
.v-divider {
  background-color: darkred;
}
.chat-suggestion {
  background-color: #176f95;
  width: 100%;
  height: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-suggestion > span {
  background-color: #176f95;
  border-radius: 4px;
  padding: 4px 12px;
  font-weight: 900;
}
#marker {
  background-color: red;
  /*width: 100%;*/
  margin: 12px 0;
  height: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}
#marker > span {
  background-color: red;
  /*border: 3px solid red;*/
  border-radius: 4px;
  padding: 2px 8px;
}

.messages-list p:last-of-type {
  margin-bottom: 0;
}

.huge {
  color: #eec43e;
  font-weight: 900;
}

.large {
  color: #80f645;
  font-weight: 500;
}

.with-aa, .with-qq, .with-kk, .unlucky {
  color: #e54654;
  font-weight: 500;
}

.set-over-set {
  color: #e54654;
  font-weight: 900;
}

.handend-div p {
  margin-bottom: 0;
}

.handend-div p:last-of-type {
  margin-bottom: 16px;
}

.mention-highlight {
  background-color: #4e013d;
  color: #ff6a1e;

  font-weight: bold;

  padding: 2px 6px;
  border-radius: 5px;
}
</style>
